"use client";

import { MapPin, ArrowRight } from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useState, useEffect, useCallback, memo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

const GalleryPopup = dynamic<any>(() => import("./HomePropertyGallery"), {
  ssr: false,
});

// Memoized image component to prevent unnecessary re-renders
const ResortImage = memo(
  ({
    src,
    alt,
    className,
    isFeatured = false,
    onClick,
  }: {
    src: string;
    alt: string;
    className: string;
    isFeatured?: boolean;
    onClick?: () => void;
  }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [optimizedSrc, setOptimizedSrc] = useState<string>("");

    useEffect(() => {
      // Create optimized image URL with parameters for better performance
      const optimizeImageUrl = (url: string) => {
        try {
          const urlObj = new URL(url);
          // Add optimization parameters
          urlObj.searchParams.set("auto", "format");
          urlObj.searchParams.set("fit", "crop");
          urlObj.searchParams.set("q", "80");
          urlObj.searchParams.set("w", "800"); // Limit width
          return urlObj.toString();
        } catch {
          return url; // Return original if URL parsing fails
        }
      };

      setOptimizedSrc(optimizeImageUrl(src));
    }, [src]);

    return (
      <div
        className={`relative overflow-hidden group ${className} pointer-events-none`}
      >
        {!isLoaded && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse" />
        )}
        <Image
          src={optimizedSrc || src}
          alt={alt}
          fill
          sizes="(max-width: 768px) 100vw, 50vw"
          className={`object-cover transition-all duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          } group-hover:scale-105`}
          style={{
            transform: "translateZ(0)",
            willChange: "transform",
          }}
          onLoad={() => setIsLoaded(true)}
          onClick={onClick}
        />

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 cursor-pointer pointer-events-none" />

        {isFeatured && (
          <div className="absolute top-4 left-4 bg-[#B42025] text-white px-4 py-2 rounded-full text-xs tracking-wider z-10">
            FEATURED
          </div>
        )}
      </div>
    );
  },
);

ResortImage.displayName = "ResortImage";

// Memoized resort item to prevent re-renders
const ResortItem = memo(
  ({
    resort,
    index,
    onOpenGallery,
    onOpenMap,
  }: {
    resort: any;
    index: number;
    onOpenGallery: (resort: any, imgIndex: number) => void;
    onOpenMap: (mapUrl: string) => void;
  }) => {
    const [isVisible, setIsVisible] = useState(false);

    // Lazy loading intersection observer
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.1, rootMargin: "50px" },
      );

      const element = document.getElementById(`resort-${index}`);
      if (element) observer.observe(element);

      return () => observer.disconnect();
    }, [index]);

    const highlights = resort?.Tags?.split(",").filter(Boolean) || [];

    if (!isVisible) {
      return (
        <div
          id={`resort-${index}`}
          className="h-[600px] bg-gray-100 animate-pulse rounded-2xl"
          aria-hidden="true"
        />
      );
    }

    return (
      <div
        id={`resort-${index}`}
        className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
          index % 2 === 1 ? "lg:flex-row-reverse" : ""
        }`}
        style={{ contentVisibility: "auto" }}
      >
        {/* Image Gallery */}
        <div className={`${index % 2 === 1 ? "lg:order-2" : ""}`}>
          <div className="grid grid-cols-3 gap-3 h-[400px] relative">
            {/* Main large image (index 0) */}
            <ResortImage
              src={resort?.BannerImages}
              alt={`${resort?.PropertyName} main view`}
              className="col-span-2 row-span-2 rounded-2xl shadow-xl cursor-pointer pointer-events-all"
              isFeatured={true}
              onClick={() => onOpenGallery(resort, 0)}
            />

            {/* Two smaller images stacked */}
            {resort?.Propertyimage?.slice(0, 2).map(
              (image: any, imgIndex: number) => (
                <ResortImage
                  key={imgIndex}
                  src={image?.PropertyGalleryImages}
                  alt={`${resort?.PropertyName} view ${imgIndex + 2}`}
                  className="rounded-xl shadow-lg cursor-pointer pointer-events-all"
                  onClick={() => onOpenGallery(resort, imgIndex + 1)}
                />
              ),
            )}

            {/* View All Gallery button if more than 3 images */}
            {resort?.Propertyimage?.length > 2 && (
              <button
                onClick={() => onOpenGallery(resort, 2)}
                className="absolute bottom-4 right-4 bg-black/70 hover:bg-black/90 text-white px-4 py-2 rounded-full text-sm transition-colors z-20"
              >
                +{resort.Propertyimage.length - 2} more
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className={`${index % 2 === 1 ? "lg:order-1" : ""}`}>
          <h3 className="text-3xl md:text-4xl mb-2 text-[#1A1A1A]">
            {resort?.PropertyName}
          </h3>

          {/* <p className="text-xl text-[#B42025] mb-4">{resort?.TagLine}</p> */}

          <div
            className="flex items-center gap-2 text-gray-600 mb-6"
            onClick={(e) => {
              if (resort?.MapURL) {
                e.preventDefault();
                onOpenMap(resort.MapURL);
              }
            }}
          >
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span
              className={`text-[#B42025] truncate ${resort?.MapURL ? "cursor-pointer" : ""}`}
            >
              {resort?.Address}
            </span>
          </div>

          <div
            className="text-gray-700 leading-relaxed mb-6 line-clamp-3"
            dangerouslySetInnerHTML={{ __html: resort?.ShortDescription }}
          />

          {/* Highlights */}
          {highlights.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {highlights.map((highlight: string, hIndex: number) => (
                <span
                  key={hIndex}
                  className="px-4 py-2 bg-white rounded-full text-sm text-gray-700 border border-gray-200"
                >
                  {highlight.trim()}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Link href={`/${resort?.PropertyURL || ""}/`}>
              <Button className="bg-[#B42025] hover:bg-[#8F1A1E] text-white group">
                Explore Resort
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>

            {/* View Gallery Button */}
            <Button
              variant="outline"
              onClick={() => onOpenGallery(resort, 0)}
              className="border-gray-300 hover:bg-gray-50"
            >
              View Gallery
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

ResortItem.displayName = "ResortItem";

export function CollectionSection({ lookUpCMSData, homeDetails }: any) {
  const properties = homeDetails?.Property || [];

  // Gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeResort, setActiveResort] = useState<any>(null);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);

  // Map state
  const [mapOpen, setMapOpen] = useState(false);
  const [activeMapURL, setActiveMapURL] = useState("");

  const handleOpenGallery = useCallback((resort: any, imgIndex: number) => {
    setActiveResort(resort);
    setCurrentGalleryIndex(imgIndex);
    setGalleryOpen(true);
  }, []);

  const handleGalleryClose = useCallback(() => {
    setGalleryOpen(false);
  }, []);

  const handleGalleryIndexChange = useCallback((index: number) => {
    setCurrentGalleryIndex(index);
  }, []);

  const handleOpenMap = useCallback((mapUrl: string) => {
    setActiveMapURL(mapUrl);
    setMapOpen(true);
  }, []);

  const handleMapClose = useCallback(() => {
    setMapOpen(false);
    setActiveMapURL("");
  }, []);

  const galleryImages = activeResort
    ? [
        { PropertyGalleryImages: activeResort?.BannerImages },
        ...(activeResort?.Propertyimage || []),
      ]
    : [];

  // Memoize the CMS content rendering
  const cmsContent = useCallback(() => {
    if (!lookUpCMSData?.["Home - Section 2 : Our Properties"]) return null;

    return (
      <div
        dangerouslySetInnerHTML={{
          __html: lookUpCMSData["Home - Section 2 : Our Properties"]?.Template,
        }}
      />
    );
  }, [lookUpCMSData]);

  // Default content
  const defaultContent = useCallback(
    () => (
      <div className="text-center mb-16">
        <p className="text-[#B42025] tracking-[0.3em] mb-4 text-sm uppercase">
          Our Properties
        </p>
        <h2 className="text-4xl md:text-5xl mb-6 text-[#1A1A1A]">
          Our Collection of Boutique Retreats
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Five distinctive destinations. One signature of quiet luxury.
        </p>
      </div>
    ),
    [],
  );

  return (
    <section id="collection" className="py-10 md:py-20 bg-[#F9F7F4]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {lookUpCMSData?.["Home - Section 2 : Our Properties"]
          ? cmsContent()
          : defaultContent()}

        <div className="space-y-12">
          {properties.map((resort: any, index: number) => (
            <ResortItem
              key={`${resort?.PropertyURL}-${index}`}
              resort={resort}
              index={index}
              onOpenGallery={handleOpenGallery}
              onOpenMap={handleOpenMap}
            />
          ))}
        </div>
      </div>

      {/* Shared Gallery Popup - Conditioned on galleryOpen to avoid unnecessary preloads */}
      {galleryOpen && activeResort && (
        <GalleryPopup
          images={galleryImages}
          currentIndex={currentGalleryIndex}
          isOpen={galleryOpen}
          onClose={handleGalleryClose}
          onIndexChange={handleGalleryIndexChange}
        />
      )}

      {/* Map Popup */}
      <Dialog open={mapOpen} onOpenChange={handleMapClose}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-white">
          <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
            <DialogTitle className="text-xl font-medium">
              Resort Location
            </DialogTitle>
          </DialogHeader>
          <div className="w-full h-[60vh] md:h-[70vh]">
            <iframe
              src={activeMapURL}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Resort Location Map"
            />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
